import { Box, Flex, Icon, IconButton, Text, VStack } from '@chakra-ui/react';
import {
  BarChart2,
  FileText,
  LucideIcon,
  PanelRightClose,
  PanelRightOpen,
  PieChart,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Artifact, useDataCoachChat } from '../DataCoachChatContext';

const ArtifactCard = ({ artifact }: { artifact: Artifact }) => {
  const getIcon = (): LucideIcon => {
    switch (artifact.type) {
      case 'chart':
        return artifact.title.includes('Monthly') ? BarChart2 : PieChart;
      case 'summary':
        return FileText;
      default:
        return BarChart2;
    }
  };

  const getPreviewContent = () => {
    switch (artifact.type) {
      case 'chart':
        if (artifact.title.includes('Monthly')) {
          return (
            <Flex align="flex-end" gap={1} h="60px" justify="center">
              {[40, 65, 45, 80, 55, 70].map((height, i) => (
                <Box
                  key={i}
                  bg="cyan.400"
                  borderRadius="sm"
                  h={`${height}%`}
                  opacity={0.8 + i * 0.03}
                  w="12px"
                />
              ))}
            </Flex>
          );
        }

        return (
          <Flex align="center" gap={2} h="60px" justify="center">
            <Box bg="purple.400" borderRadius="full" h="50px" w="50px" />
            <VStack align="flex-start" spacing={1}>
              <Box bg="purple.400" borderRadius="sm" h="8px" w="40px" />
              <Box bg="cyan.400" borderRadius="sm" h="8px" w="30px" />
              <Box bg="cyan.300" borderRadius="sm" h="8px" w="35px" />
            </VStack>
          </Flex>
        );
      case 'summary':
        return (
          <VStack align="stretch" h="60px" justify="center" spacing={2}>
            <Box bg="whiteAlpha.200" borderRadius="sm" h="6px" w="100%" />
            <Box bg="whiteAlpha.200" borderRadius="sm" h="6px" w="80%" />
            <Box bg="whiteAlpha.200" borderRadius="sm" h="6px" w="90%" />
          </VStack>
        );
      default:
        return null;
    }
  };

  return (
    <Box
      _hover={{
        borderColor: 'cyan.500',
        transform: 'translateY(-2px)',
        shadow: 'md',
      }}
      bg="card.bg"
      border="1px solid"
      borderColor="card.border"
      borderRadius="lg"
      cursor="pointer"
      p={4}
      transition="all 0.2s"
    >
      <Flex align="center" gap={2} mb={3}>
        <Icon as={getIcon()} color="cyan.400" />
        <Text fontSize="sm" fontWeight="medium">
          {artifact.title}
        </Text>
      </Flex>
      {getPreviewContent()}
    </Box>
  );
};

export const ArtifactsSidebar = () => {
  const { selectedConversation } = useDataCoachChat();
  const artifacts = selectedConversation?.artifacts || [];
  const [isCollapsed, setIsCollapsed] = useState(artifacts.length === 0);

  useEffect(() => {
    setIsCollapsed(artifacts.length === 0);
  }, [selectedConversation?.id]);

  return (
    <Flex
      bg="bg.main"
      borderLeft="1px solid"
      borderLeftColor="border.divider"
      direction="column"
      h="100%"
      overflow="hidden"
      transition="width 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
      w={isCollapsed ? '48px' : '280px'}
    >
      {/* Header */}
      <Flex
        align="center"
        borderBottom="1px solid"
        borderBottomColor="border.divider"
        h="49.5px"
        justify={isCollapsed ? 'center' : 'space-between'}
        px={isCollapsed ? 0 : 4}
      >
        {!isCollapsed && (
          <Text fontSize="lg" fontWeight="semibold" whiteSpace="nowrap">
            Artifacts
          </Text>
        )}
        <IconButton
          aria-label={isCollapsed ? 'Expand artifacts' : 'Collapse artifacts'}
          icon={
            isCollapsed ? (
              <PanelRightOpen size={16} />
            ) : (
              <PanelRightClose size={16} />
            )
          }
          size="xs"
          variant="ghost"
          onClick={() => setIsCollapsed((prev) => !prev)}
        />
      </Flex>

      {/* Artifacts List */}
      {!isCollapsed && (
        <VStack align="stretch" flex={1} overflowY="auto" p={4} spacing={3}>
          {artifacts.length === 0 ? (
            <Text color="whiteAlpha.400" fontSize="sm" textAlign="center">
              No artifacts yet. Start a conversation to generate visualizations
              and insights.
            </Text>
          ) : (
            artifacts.map((artifact) => (
              <ArtifactCard key={artifact.id} artifact={artifact} />
            ))
          )}
        </VStack>
      )}
    </Flex>
  );
};
